import { resize, ResizeMode } from '@wix/thumbnail-generator';
import classnames from 'classnames';
import { head } from 'ramda';
import React, { FC, useMemo } from 'react';
import { ResizedImage } from '../../../..';
import { useVideoPreviews } from '../../../../../../utils/media-manager-sdk/hooks';
import { getVideoThumbnail, isItemFromVimeoCreator } from '../../../../../../utils/media-manager-sdk/items';
import {
  isFailed as isFileFailed,
  isReady as isFileReady,
  isTranscoding as isFileTranscoding,
} from '../../../../../../utils/media-manager-sdk/transcoding';
import { ErrorOverlay, TranscodingOverlay } from '../../../../TranscodingOverlays';
import styles from '../../FileThumbnail.scss';
import videoThumbnailStyles from './VideoThumbnail.scss';
import { IOuter } from './VideoThumbnail.types';

export const VideoThumbnail: FC<IOuter> = ({
  file,
  className,
  width,
  height,
  dataHook = 'video-file-thumbnail',
  params = {},
  style,
  playVideoOnHover,
  videoNodeRef,
  saveRef,
  shouldUsePictureBackgroundImageImmediately,
  wrapperClassName,
  thumbnailBackground,
  isAlphaVideo,
  canvasNodeRef,
  isPlaying,
}) => {
  const videoSrc = head(useVideoPreviews(file));

  const {
    canPlayVideoOnHover,
    isTranscoding,
    isFailed,
    videoThumbnail,
    videoPoster,
    canvasStyle,
    isVideoFromVimeoCreator,
  } = useMemo(() => {
    const videoThumbnail = getVideoThumbnail(file);

    return {
      canPlayVideoOnHover: playVideoOnHover && isFileReady(file),
      isTranscoding: isFileTranscoding(file),
      isFailed: isFileFailed(file),
      isVideoFromVimeoCreator: isItemFromVimeoCreator(file),
      videoThumbnail,
      videoPoster: resize(videoThumbnail, { width, height, mode: ResizeMode.Fill }),
      canvasStyle: { maxHeight: height, maxWidth: width, flex: `0 0 auto`, alignSelf: 'center' },
    };
  }, [playVideoOnHover, file, width, height]);

  const resizeImageStyle = useMemo(
    () => ({ ...style, backgroundColor: thumbnailBackground }),
    [style, thumbnailBackground],
  );

  const resizeImageParams = useMemo(() => ({ mode: ResizeMode.Fit, ...params }), [params]);

  const videoStyle = useMemo(() => ({ display: isAlphaVideo ? 'none' : 'block' }), [isAlphaVideo]);

  return (
    <div className={classnames(styles.thumbnailWrapper, wrapperClassName)} style={{ height }} ref={saveRef}>
      {canPlayVideoOnHover && (
        <div className={styles.videoOnHoverWrapper}>
          <video
            className={videoThumbnailStyles.video}
            src={videoSrc}
            poster={videoPoster}
            loop
            muted
            controls={false}
            width={width}
            ref={videoNodeRef}
            style={videoStyle}
            crossOrigin="anonymous"
          />
          {isAlphaVideo && (
            <div className={videoThumbnailStyles.canvasWrapper}>
              <canvas ref={canvasNodeRef} style={canvasStyle} />
            </div>
          )}
        </div>
      )}

      <ResizedImage
        className={classnames(
          styles.thumbnail,
          styles.video,
          { [styles.transcoding]: isTranscoding, [styles.playing]: isPlaying },
          className,
        )}
        src={videoThumbnail}
        isSecure={false}
        source={file.source}
        width={width}
        height={height}
        style={resizeImageStyle}
        params={resizeImageParams}
        shouldUseBackgroundImageImmediately={shouldUsePictureBackgroundImageImmediately}
        dataHook={dataHook}
      />
      {isVideoFromVimeoCreator && <span className={videoThumbnailStyles.vimeo} />}
      {isTranscoding && <TranscodingOverlay />}
      {isFailed && <ErrorOverlay />}
    </div>
  );
};
